<script setup>
    import { ref, watch } from 'vue';
    import axios from 'axios';
    import * as bootstrap from 'bootstrap';
    import '@vuepic/vue-datepicker/dist/main.css'

    const props = defineProps(['copyUrl', 'participantData', 'campaigns']);
    const emit = defineEmits(['reloadPage']);
    const teilnehmer = ref({
        Rechnungsempfaenger: props.participantData.customerNr,
        Teilnehmer: props.participantData.participantNr,
        Vorname: props.participantData.name,
        Name: props.participantData.givenname,
        Kartennummer: '',
        rfid: '',
        charging_rate_id: '',
        campaign: props.participantData.campaign ?? '',
        dienstausweisnummer: ''
    });

    const submitResult = ref(null);
    const submitError = ref(null);
    const isLoading = ref(null);
    const showInfo = ref(null);
    const campaigns = ref([]);

    if (props.campaigns) {
        try {
            campaigns.value = JSON.parse(props.campaigns);
        } catch (e) {
            campaigns.value = [];
        }
    }

    watch(props.participantData, (data, prevData) => {
            teilnehmer.value.Rechnungsempfaenger = data.customerNr;
            teilnehmer.value.Teilnehmer = data.participantNr;
            teilnehmer.value.Vorname = data.name;
            teilnehmer.value.Name = data.givenname;
            teilnehmer.value.campaign = data.campaign ?? '';
        }
    )

    // Loads a bootstrap modal & saving the new teilnehmer.
    async function saveTeilnehmer() {
        try {
            isLoading.value = true;
            showInfo.value = true;


            if (!teilnehmer.value.Kartennummer && !teilnehmer.value.rfid) {
                submitError.value = { response: { data: { Kartennummer: ['Mindestens Kartennummer oder RFID erforderlich.'] } } };
                isLoading.value = null;
                showInfo.value = null;
                return;
            }

            const response = await axios.post(props.copyUrl, teilnehmer.value);
            submitResult.value = response;

            // Close the first modal after a one-second delay
            setTimeout(() => {
                const currentModal = document.querySelector('#modalTeilnehmerCopy');
                const modal = bootstrap.Modal.getInstance(currentModal);
                modal.hide();
                // Reload the page after a two-second delay
                setTimeout(() => {
                    teilnehmer.value.Vorname = props.participantData.name;
                    teilnehmer.value.Name = props.participantData.givenname;
                    teilnehmer.value.Kartennummer = '';
                    teilnehmer.value.dienstausweisnummer = '';
                    isLoading.value = null;
                    showInfo.value = null;
                    emit('reloadPage');
                }, 1000);
            }, 1000);
        } catch (error) {
            isLoading.value = null;
            showInfo.value = null;
            submitError.value = error;
            setTimeout(() => {
                submitError.value = null;
            }, 3000);
        }
    }

    // Loads a bootstrap modal & saving the new teilnehmer.
    async function saveTeilnehmerContinue() {
        try {
            isLoading.value = true;
            showInfo.value = true;


            if (!teilnehmer.value.Kartennummer && !teilnehmer.value.rfid) {
                submitError.value = { response: { data: { Kartennummer: ['Mindestens Kartennummer oder RFID erforderlich.'] } } };
                isLoading.value = null;
                showInfo.value = null;
                return;
            }

            const response = await axios.post(props.copyUrl, teilnehmer.value);
            submitResult.value = response;

            // Close the first modal after a one-second delay
            setTimeout(() => {
                teilnehmer.value.Vorname = props.participantData.name;
                teilnehmer.value.Name = props.participantData.givenname;
                teilnehmer.value.Kartennummer = '';
                teilnehmer.value.dienstausweisnummer = '';
                isLoading.value = null;
                showInfo.value = null;
            }, 1000);
        } catch (error) {
            isLoading.value = null;
            showInfo.value = null;
            submitError.value = error;
            setTimeout(() => {
                submitError.value = null;
            }, 3000);
        }
    }

    // Prefill charging_rate_id if original participant has an auth media
    async function prefillChargingRate() {
        try {
            const re = props.participantData.customerNr;
            const columns = 'id,identifier,Rechnungsempfaengernummer,Teilnehmer,charging_rate_id,bicycle_rate_id';
            const url = `/api/authMedia/start/table/${re}?page=1&per_page=25&columns=${columns}&column=Rechnungsempfaengernummer&order=desc`;
            const res = await axios.get(url);
            const list = res && res.data && res.data.data ? res.data.data : (res && res.data ? res.data : []);
            const media = list.find(m => String(m.Teilnehmer) === String(props.participantData.participantNr));
            if (media && media.charging_rate_id) {
                teilnehmer.value.charging_rate_id = media.charging_rate_id;
            }
        } catch (e) {
            // ignore
        }
    }

    prefillChargingRate();





</script>

<template>
    <div>
    <form>
        <div class="row">
            <div class="col-sm-4 mb-2">
                <label for="kartennummer">Kartennummer</label>
                <input id="kartennummer"
                    type="text"
                    v-model="teilnehmer.Kartennummer"
                    :class="{
                        'form-control': true,
                        'is-valid': (submitResult !== null),
                        'is-invalid': (submitError !== null && typeof submitError.response.data.Kartennummer !== 'undefined'),
                    }"
                />
                <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Kartennummer !== 'undefined')" v-html="submitError.response.data.Kartennummer[0]"></div>
            </div>

            <div class="col-sm-4 mb-2">
                <label for="rfid">RFID</label>
                <input id="rfid"
                    type="text"
                    v-model="teilnehmer.rfid"
                    :class="{
                        'form-control': true,
                        'is-valid': (submitResult !== null),
                        'is-invalid': (submitError !== null && typeof submitError.response.data.rfid !== 'undefined'),
                    }"
                />
                <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.rfid !== 'undefined')" v-html="submitError.response.data.rfid[0]"></div>
            </div>

            <div class="col-sm-4 mb-2">
                <label for="charging_rate_id">Ladetarife</label>
                <select id="charging_rate_id"
                    v-model="teilnehmer.charging_rate_id"
                    :class="{
                        'form-select form-control': true,
                        'is-valid': (submitResult !== null),
                        'is-invalid': (submitError !== null && typeof submitError.response.data.charging_rate_id !== 'undefined'),
                    }"
                >
                    <option value="">kein Ladetarif ausgewählt</option>
                    <option value="2">Mobility Key Aachen</option>
                    <option value="7">Mobility Key Aachen Park-Flat</option>
                    <option value="5">Mobility Key Berlin</option>
                    <option value="3">Mobility Key Plus</option>
                    <option value="4">Mobility Key Flat</option>
                </select>
                <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.charging_rate_id !== 'undefined')" v-html="submitError.response.data.charging_rate_id[0]"></div>
            </div>

            <div class="col-sm-4 mb-2">
                <label for="campaign">Kampagne</label>
                <select disabled id="campaign" v-model="teilnehmer.campaign" class="form-select">
                    <option value=""> -- keine -- </option>
                    <option v-for="campaign in campaigns" :value="campaign">{{ campaign }}</option>
                </select>
            </div>

            <div v-if="teilnehmer.campaign" class="col-sm-4 mb-2">
                <label for="dienstausweisnummer">Dienstausweisnummer</label>
                <input id="dienstausweisnummer" type="text" v-model="teilnehmer.dienstausweisnummer"
                    :class="{
                        'form-control': true,
                        'is-valid': (submitResult !== null),
                        'is-invalid': (submitError !== null && typeof submitError.response.data.dienstausweisnummer !== 'undefined'),
                    }"
                />
                <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.dienstausweisnummer !== 'undefined')" v-html="submitError.response.data.dienstausweisnummer[0]"></div>
            </div>

            <div class="col-sm-4 mb-2">
                <label for="vorname">Vorname (*)</label>
                <input id="vorname"
                    type="text"
                    v-model="teilnehmer.Vorname"
                    :class="{
                        'form-control': true,
                        'is-valid': (submitResult !== null),
                        'is-invalid': (submitError !== null && typeof submitError.response.data.Vorname !== 'undefined'),
                    }"
                />
                <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Vorname !== 'undefined')" v-html="submitError.response.data.Vorname[0]"></div>
            </div>

            <div class="col-sm-4">
                <label for="name">Name (*)</label>
                <input id="name"
                    type="text"
                    v-model="teilnehmer.Name"
                    :class="{
                        'form-control': true,
                        'is-valid': (submitResult !== null),
                        'is-invalid': (submitError !== null && typeof submitError.response.data.Name !== 'undefined'),
                    }"
                />
                <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Name !== 'undefined')" v-html="submitError.response.data.Name[0]"></div>
            </div>





        </div>
        <p>Felder mit einem * sind Pflichtfelder.</p>
        <button type="button" class="btn btn-primary" @click.prevent="saveTeilnehmer()">Teilnehmer hinzufügen
            <i :class="[isLoading ? 'fa-solid fa-spinner fa-spin' : '']" aria-hidden="true"></i>
        </button>
        <button type="button" class="btn btn-primary" @click.prevent="saveTeilnehmerContinue()">Teilnehmer hinzufügen und weitermachen
            <i :class="[isLoading ? 'fa-solid fa-spinner fa-spin' : '']" aria-hidden="true"></i>
        </button>
        <p v-if="showInfo" class="text-warning">If saving takes too long, check S&B api connection. Await 60s.</p>
    </form>
    <div class="alert alert-success" role="alert" v-if="submitResult !== null && (submitResult.status === 201 || submitResult.status === 200)">
        <span>Teilnehmer erfolgreich hinzugefügt.</span>
    </div>
    <div class="alert alert-error" role="alert" v-if="submitError != null">
        <span>{{submitError.response.data.message}}</span>
    </div>
</div>
</template>
